import React, { useState, useEffect } from 'react';
import { 
  Box, Heading, VStack, Text, Divider, 
  Flex, Link, Spinner, useColorModeValue,
  Alert, AlertIcon
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { ArrowForwardIcon } from '@chakra-ui/icons';
import { supabase } from '../../integrations/supabase/client';

const SoloCompanyNews = () => {
  const [articles, setArticles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  
  // Color settings
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const color = "teal.500"; // Teal theme for Solo Company News
  
  // Fixed card height
  const CARD_HEIGHT = "400px";
  
  // 监听网络状态变化
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  useEffect(() => {
    const fetchSoloCompanyArticles = async () => {
      if (isOffline) {
        setIsLoading(false);
        setError("网络连接已断开");
        return;
      }
      
      setIsLoading(true);
      try {
        // 1. Get all feed_ids with category "一人公司动态" from feed table
        const { data: feedsData, error: feedsError } = await supabase
          .from('feed')
          .select('feed_id')
          .eq('category', '一人公司动态');
          
        if (feedsError) throw feedsError;
        
        // If no matching feeds found
        if (!feedsData || feedsData.length === 0) {
          setError('No "一人公司动态" category found, please add this category to the database first');
          setArticles([]);
          setIsLoading(false);
          return;
        }
        
        // Extract feed_id list
        const feedIds = feedsData.map(feed => feed.feed_id);
        console.log('Retrieved 一人公司动态 feedIds:', feedIds);
        
        // 2. Use feed_id to get articles from article table
        const { data: articlesData, error: articlesError } = await supabase
          .from('article')
          .select('*')
          .in('feed_id', feedIds)
          .order('published_at', { ascending: false })
          .limit(20); // Get more articles for scrolling
          
        if (articlesError) throw articlesError;
        
        console.log(`Retrieved ${articlesData?.length || 0} 一人公司动态 articles`);
        
        // If no articles found, show error message
        if (!articlesData || articlesData.length === 0) {
          setError('No 一人公司动态 articles found, please ensure there are related articles in the database');
        }
        
        setArticles(articlesData || []);
      } catch (error) {
        console.error('Failed to fetch 一人公司动态 articles:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSoloCompanyArticles();
  }, [isOffline]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  // Truncate text
  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (isLoading) {
    return (
      <Box 
        bg={bgColor} 
        borderWidth="1px" 
        borderColor={borderColor}
        borderRadius="lg" 
        p={6} 
        height={CARD_HEIGHT}
      >
        <Heading 
          size="md" 
          mb={4} 
          color={color}
          pb={2}
          borderBottom={`2px solid ${color}`}
        >
          Solo Company News
        </Heading>
        <Flex justify="center" align="center" height="calc(100% - 60px)">
          <Spinner color={color} size="xl" />
        </Flex>
      </Box>
    );
  }
  
  if (isOffline) {
    return (
      <Box 
        bg={bgColor} 
        borderWidth="1px" 
        borderColor={borderColor}
        borderRadius="lg" 
        p={6} 
        height={CARD_HEIGHT}
      >
        <Heading 
          size="md" 
          mb={4} 
          color={color}
          pb={2}
          borderBottom={`2px solid ${color}`}
        >
          Solo Company News
        </Heading>
        <Flex justify="center" align="center" height="calc(100% - 60px)" direction="column">
          <Alert status="warning" mb={4}>
            <AlertIcon />
            网络连接已断开
          </Alert>
          <Text color="gray.500" fontSize="sm">
            请检查您的网络连接后重试
          </Text>
        </Flex>
      </Box>
    );
  }

  if (error && articles.length === 0) {
    return (
      <Box 
        bg={bgColor} 
        borderWidth="1px" 
        borderColor={borderColor}
        borderRadius="lg" 
        p={6} 
        height={CARD_HEIGHT}
      >
        <Heading as="h2" size="md" mb={4} color={color}>
          Solo Company News
        </Heading>
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <Text fontWeight="bold">No Data Available</Text>
            <Text fontSize="sm">
              {error || 'No Solo Company News articles available'}
            </Text>
          </Box>
        </Alert>
      </Box>
    );
  }

  return (
    <Box 
      bg={bgColor} 
      borderWidth="1px" 
      borderColor={borderColor}
      borderRadius="lg" 
      p={6} 
      height={CARD_HEIGHT}
      position="relative"
      overflow="hidden"
    >
      <Heading as="h2" size="md" mb={4} color={color}>
        Solo Company News
      </Heading>
      
      {/* Scrollable content area */}
      <Box 
        height="calc(100% - 80px)" 
        overflowY="auto"
        css={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            width: '6px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: color,
            borderRadius: '24px',
          },
        }}
      >
        <VStack spacing={4} align="stretch">
          {articles.slice(0, 10).map((article, index) => (
            <Box key={article.id || index}>
              <Link 
                as={RouterLink} 
                to={`/article/${article.article_id}`}
                _hover={{ textDecoration: 'none' }}
              >
                <Box _hover={{ bg: useColorModeValue("gray.50", "gray.700") }} p={2} borderRadius="md">
                  <Text 
                    fontWeight="semibold" 
                    fontSize="sm" 
                    lineHeight="1.3"
                    color={useColorModeValue("gray.800", "gray.100")}
                    noOfLines={2}
                    mb={1}
                  >
                    {article.title}
                  </Text>
                  {article.description && (
                    <Text 
                      fontSize="xs" 
                      color={useColorModeValue("gray.600", "gray.400")}
                      noOfLines={2}
                      mb={1}
                    >
                      {truncateText(article.description, 80)}
                    </Text>
                  )}
                  <Text fontSize="xs" color={color}>
                    {formatDate(article.published_at)}
                  </Text>
                </Box>
              </Link>
              {index < articles.slice(0, 10).length - 1 && <Divider />}
            </Box>
          ))}
        </VStack>
      </Box>

      {/* Bottom shadow gradient to indicate scrollable content */}
      {articles.length > 4 && (
        <Box 
          position="absolute" 
          bottom="0" 
          left="0" 
          right="0" 
          height="40px" 
          bgGradient={`linear(to-t, ${bgColor}, transparent)`}
          pointerEvents="none"
          borderBottomRadius="lg"
        />
      )}
      
      {/* View more link positioned above bottom shadow area */}
      <Flex 
        justify="flex-end" 
        position="absolute" 
        bottom="5" 
        right="5"
        zIndex="1"
      >
        <Link 
          as={RouterLink} 
          to={`/category/一人公司动态`}
          color={color}
          fontSize="sm"
          fontWeight="medium"
          display="flex"
          alignItems="center"
          bg={bgColor}
          px={2}
          borderRadius="md"
        >
          View More
          <ArrowForwardIcon ml={1} />
        </Link>
      </Flex>
    </Box>
  );
};

export default SoloCompanyNews;
